// src/services/poPdfService.ts
// Generates a print-quality Purchase Order document using HTML + window.print()
// No external dependencies — browser renders and the user saves as PDF via the print dialog.
//
// HP2 WIRED: reads company identity, accent colour, font, logo, and footer settings
// from documentBranding.ts via buildDocumentHeader() / buildDocumentFooter() /
// getDocumentBranding() — so any branding change in Settings immediately reflects
// in every PO printed from that point forward.

import {
  buildDocumentHeader,
  buildDocumentFooter,
  getDocumentBranding,
} from '../utils/documentBranding'

const STATUS_COLOR: Record<string, string> = {
  DRAFT:               '#6b7280',
  PENDING_APPROVAL:    '#d97706',
  APPROVED:            '#2563eb',
  SENT:                '#7c3aed',
  PARTIALLY_DELIVERED: '#ea580c',
  DELIVERED:           '#16a34a',
  REJECTED:            '#dc2626',
}

function fmtAUD(n: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(n)
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

export function printPODocument(po: any, job: any): void {
  const b           = getDocumentBranding()
  const accent      = b.docTypeOverrides['PURCHASE_ORDER']?.accentColor ?? b.accentColor
  const font        = b.fontFamily
  const now         = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const poRef       = po.poNumber ?? po.id ?? 'PO-DRAFT'
  const gst         = (po.totalExGST ?? 0) * 0.1
  const totalIncGST = (po.totalExGST ?? 0) + gst
  const statusCol   = STATUS_COLOR[po.status as string] ?? '#6b7280'
  const statusLabel = (po.status ?? 'DRAFT').replace(/_/g, ' ')

  // Normalise line items — fall back to a single summary line for header-only POs
  const lineItems: any[] =
    po.lineItems && po.lineItems.length > 0
      ? po.lineItems
      : [{
          partNumber:  '',
          description: po.description ?? 'See description above',
          quantity:    1,
          unitPrice:   po.totalExGST ?? 0,
          totalExGST:  po.totalExGST ?? 0,
        }]

  const lineRows = lineItems
    .map((l: any, i: number) => `
      <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f9fafb'}">
        <td style="padding:7px 10px;font-family:monospace;font-size:9pt;color:#888;white-space:nowrap">${l.partNumber || '—'}</td>
        <td style="padding:7px 10px;font-size:9.5pt">${l.description ?? ''}</td>
        <td style="padding:7px 8px;text-align:center;font-size:9.5pt;font-family:monospace">${l.quantity ?? 1}</td>
        <td style="padding:7px 8px;text-align:right;font-size:9.5pt;font-family:monospace">${fmtAUD(l.unitPrice ?? 0)}</td>
        <td style="padding:7px 10px;text-align:right;font-size:9.5pt;font-family:monospace;font-weight:600">${fmtAUD(l.totalExGST ?? 0)}</td>
      </tr>`)
    .join('')

  // documentBranding.ts header and footer — HP2 wired
  const headerHtml = buildDocumentHeader('PURCHASE_ORDER', poRef)
  const footerHtml = buildDocumentFooter('PURCHASE_ORDER', poRef, po.authorisedByName)

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Purchase Order ${poRef}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: ${font}, sans-serif;
      font-size: 10pt;
      color: #111;
      background: #fff;
      margin: 14mm;
    }

    /* ── PO title row (placed after branding header) ── */
    .po-title-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: 18px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
    }
    .po-title-label { font-size: 17pt; font-weight: 900; color: ${accent}; }
    .po-ref         { font-size: 13pt; font-weight: 700; color: ${accent}; margin-top: 3px; }
    .po-badge {
      display: inline-block;
      font-size: 8pt;
      font-weight: 700;
      padding: 2px 9px;
      border-radius: 20px;
      margin-top: 5px;
      color: ${statusCol};
      border: 1.5px solid ${statusCol};
    }

    /* ── Two-column meta ── */
    .meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-bottom: 18px;
    }
    .meta-box {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 12px 14px;
    }
    .supplier-box { border-color: ${accent}; border-width: 1.5px; }
    .meta-box-title {
      font-size: 7.5pt;
      font-weight: 700;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 8px;
    }
    .supplier-name { font-size: 12pt; font-weight: 700; color: ${accent}; }
    .supplier-sub  { font-size: 8.5pt; color: #6b7280; margin-top: 3px; }
    .meta-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 9.5pt;
      margin-bottom: 5px;
      gap: 8px;
    }
    .meta-label { color: #6b7280; white-space: nowrap; }
    .meta-value { font-weight: 600; color: #111; text-align: right; font-size: 9pt; }

    /* ── Description ── */
    .desc-box {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 10px 14px;
      margin-bottom: 16px;
    }
    .desc-label {
      font-size: 7.5pt;
      font-weight: 700;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 5px;
    }
    .desc-text { font-size: 10pt; color: #111; line-height: 1.5; }

    /* ── Line items table ── */
    table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    thead th {
      background: ${accent};
      color: #fff;
      padding: 8px 10px;
      font-size: 9pt;
      font-weight: 600;
      text-align: left;
    }
    thead th.r { text-align: right; }
    thead th.c { text-align: center; }
    tbody td { border-bottom: 1px solid #f3f4f6; vertical-align: top; }

    /* ── Totals ── */
    .totals-wrap { display: flex; justify-content: flex-end; margin-bottom: 18px; }
    .totals-table {
      width: 290px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      overflow: hidden;
      border-collapse: separate;
      border-spacing: 0;
    }
    .totals-table td { padding: 6px 14px; border-bottom: 1px solid #f3f4f6; font-size: 9.5pt; }
    .totals-table tfoot td {
      background: ${accent};
      color: #fff;
      font-weight: 700;
      font-size: 11pt;
      border-bottom: none;
      padding: 8px 14px;
    }

    /* ── Notes ── */
    .notes-box {
      border: 1px dashed #d1d5db;
      border-radius: 6px;
      padding: 10px 12px;
      font-size: 9pt;
      color: #555;
      margin-bottom: 20px;
    }

    /* ── Authorisation ── */
    .auth-section { border-top: 1px solid #e5e7eb; padding-top: 16px; margin-top: 4px; }
    .auth-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
    .auth-name { font-size: 10.5pt; font-weight: 700; color: #111; margin-bottom: 5px; }
    .auth-line { border-top: 1px solid #111; padding-top: 5px; font-size: 8.5pt; color: #6b7280; }

    @media print {
      body { margin: 10mm; }
      @page { margin: 10mm; size: A4 portrait; }
    }
  </style>
</head>
<body>

  <!-- Branding header (from documentBranding.ts — Settings / Branding controls this) -->
  ${headerHtml}

  <!-- PO title + status badge -->
  <div class="po-title-row">
    <div>
      <div class="po-title-label">PURCHASE ORDER</div>
      <div class="po-ref">${poRef}</div>
    </div>
    <div style="text-align:right">
      <span class="po-badge">${statusLabel}</span>
      <div style="font-size:8.5pt;color:#6b7280;margin-top:6px;">Issued ${now}</div>
    </div>
  </div>

  <!-- Supplier + Order meta -->
  <div class="meta-grid">
    <div class="meta-box supplier-box">
      <div class="meta-box-title">Supplier</div>
      <div class="supplier-name">${po.supplier || 'To be confirmed'}</div>
      ${po.supplierABN     ? `<div class="supplier-sub">ABN ${po.supplierABN}</div>` : ''}
      ${po.supplierAddress ? `<div class="supplier-sub">${po.supplierAddress}</div>` : ''}
      ${po.supplierEmail   ? `<div class="supplier-sub">${po.supplierEmail}</div>` : ''}
    </div>
    <div class="meta-box">
      <div class="meta-box-title">Order details</div>
      ${job ? `<div class="meta-row"><span class="meta-label">Job number</span><span class="meta-value">${job.jobNumber}</span></div>` : ''}
      ${job ? `<div class="meta-row"><span class="meta-label">Site / project</span><span class="meta-value" style="font-size:8.5pt;max-width:160px;text-align:right">${job.siteAddress || job.description || '—'}</span></div>` : ''}
      ${job?.clientName    ? `<div class="meta-row"><span class="meta-label">Client</span><span class="meta-value">${job.clientName}</span></div>` : ''}
      ${po.deliveryDate    ? `<div class="meta-row"><span class="meta-label">Required by</span><span class="meta-value">${fmtDate(po.deliveryDate)}</span></div>` : ''}
      ${po.authorisedByName ? `<div class="meta-row"><span class="meta-label">Authorised by</span><span class="meta-value">${po.authorisedByName}</span></div>` : ''}
      ${po.authorisedAt    ? `<div class="meta-row"><span class="meta-label">Authorised on</span><span class="meta-value">${fmtDate(po.authorisedAt)}</span></div>` : ''}
    </div>
  </div>

  <!-- Description of works -->
  ${po.description ? `
  <div class="desc-box">
    <div class="desc-label">Description of works / supply</div>
    <div class="desc-text">${po.description}</div>
  </div>` : ''}

  <!-- Line items -->
  <table>
    <thead>
      <tr>
        <th style="width:90px">Part / Code</th>
        <th>Description</th>
        <th class="c" style="width:50px">Qty</th>
        <th class="r" style="width:105px">Unit price</th>
        <th class="r" style="width:115px">Total (ex GST)</th>
      </tr>
    </thead>
    <tbody>${lineRows}</tbody>
  </table>

  <!-- Totals -->
  <div class="totals-wrap">
    <table class="totals-table">
      <tbody>
        <tr>
          <td style="color:#6b7280">Subtotal (ex GST)</td>
          <td style="text-align:right;font-family:monospace;font-weight:600">${fmtAUD(po.totalExGST ?? 0)}</td>
        </tr>
        <tr>
          <td style="color:#6b7280">GST (10%)</td>
          <td style="text-align:right;font-family:monospace">${fmtAUD(gst)}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td>TOTAL (inc GST)</td>
          <td style="text-align:right;font-family:monospace">${fmtAUD(totalIncGST)}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- Notes -->
  ${po.notes ? `
  <div class="notes-box">
    <strong>Delivery / special instructions:</strong>&nbsp; ${po.notes}
  </div>` : ''}

  <!-- Authorisation blocks -->
  <div class="auth-section">
    <div class="auth-grid">
      <div>
        <div class="auth-name">${po.authorisedByName ?? '&nbsp;'}</div>
        <div class="auth-line">Authorised by &nbsp;·&nbsp; ${po.authorisedAt ? fmtDate(po.authorisedAt) : 'Date'}</div>
      </div>
      <div>
        <div class="auth-name">&nbsp;</div>
        <div class="auth-line">Supplier acknowledgement &nbsp;·&nbsp; Date received</div>
      </div>
    </div>
  </div>

  <!-- Branding footer (from documentBranding.ts — Settings / Branding controls this) -->
  ${footerHtml}

</body>
</html>`

  const w = window.open('', '_blank', 'width=960,height=1100')
  if (!w) {
    alert('Pop-up blocked — please allow pop-ups for this site to print PO documents.')
    return
  }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 450)
}
